import { cn } from "@/lib/utils";

/**
 * A number in a small gray pill: how many items a section, a tab or a button
 * holds ("Rubrics 12", "View comments 3"). It sits after the label it counts
 * and is never clickable on its own. `size="count"` on `Badge` is a different
 * thing: a toned chip that carries its own noun ("3 tests").
 *
 * It was written by hand in a dozen places with two greys and two sizes.
 * `primitive-shapes.mjs` registers its shape, so
 * `tests/unit/primitiveLookalikes.test.js` fails on a new hand-built copy.
 *
 * Inside a segmented tab (`onRail`) it is a shade darker than the gray rail,
 * and on the selected tab, which is white, it is the heading chip again: the
 * white tab already says which one is selected, so the count adds no colour.
 * It reads "selected" from the tab's `aria-selected`, which Radix triggers set
 * and a hand-built `role="tab"` button must set too.
 *
 * `className` adds layout only (`ml-auto`). Renders nothing for a missing
 * count; a zero still shows.
 *
 * @param {Object} props
 * @param {number} props.count
 * @param {boolean} [props.onRail] Sits in a segmented tab on the gray rail.
 * @param {string} [props.className]
 */
export default function CountChip({ count, onRail = false, className }) {
  if (count == null) return null;
  return (
    <span
      className={cn(
        "rounded-full bg-gray-100 px-1.5 text-[11px] font-semibold tabular-nums text-gray-600",
        onRail && "bg-gray-200/70 in-aria-selected:bg-gray-100",
        className,
      )}
    >
      {count}
    </span>
  );
}
